import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { Button, ButtonBar, InfoLabel } from '../../../../common/components';
import EditDocumentModal from '../../../productManagement/components/DocumentManagement/EditDocumentModal/EditDocumentModal';
import { actions as documentActions } from '../../../productManagement/redux/DocumentReducer';
import formatDateTime from '../../../../helpers/formatDateTime';
import './BrokerToba.scss';
import DocumentIsGenerating from '../../../productManagement/components/DocumentManagement/DocumentIsGenerating/DocumentIsGenerating';
import FaFilePdf from 'react-icons/lib/fa/file';
import FaRefresh from 'react-icons/lib/fa/refresh';
import DocumentVersionSelector from '../../../productManagement/components/DocumentManagement/DocumentVersionSelector/DocumentVersionSelector';

const LABEL_SIZE = 2;

const TobaFields = ({ activeAttributes }) => (
  <>
    <InfoLabel label="Name" value={activeAttributes.name} labelSize={LABEL_SIZE} />
    <InfoLabel label="Status" value={activeAttributes.status} labelSize={LABEL_SIZE} />
    <InfoLabel
      label="Version"
      value={activeAttributes.version_number}
      labelSize={LABEL_SIZE}
    />
    <InfoLabel
      label="Display Name"
      value={activeAttributes.display_name}
      labelSize={LABEL_SIZE}
    />
    <InfoLabel
      label="From Date"
      value={formatDateTime(activeAttributes.from_date)}
      labelSize={LABEL_SIZE}
    />
    <InfoLabel
      label="To Date"
      value={formatDateTime(activeAttributes.to_date)}
      labelSize={LABEL_SIZE}
    />
    <InfoLabel
      label="File"
      value={
        <a
          target="_blank"
          className={'btn btn-primary btn-xs broker-toba--file-btn'}
          href={activeAttributes.file_path}
        >
          <i className="fa fa-file" />
          {activeAttributes.short_name}
        </a>
      }
      labelSize={LABEL_SIZE}
    />
  </>
);

const EndedTobas = ({ show, document }) => {
  if (!show || !document) {
    return null;
  }

  return (
    <>
      Ended Toba
      <Row>
        <Col sm={12}>
          <TobaFields activeAttributes={document} />
        </Col>
      </Row>
    </>
  );
};
const NoSupersedingToba = ({ show, onShowAddDocumentModal }) => {
  if (!show) {
    return null;
  }
  return (
    <>
      <h2>No Broker Specific Document Found</h2>
      <ButtonBar>
        <Button
          bsStyle={'primary'}
          className={'pull-right'}
          handleClick={onShowAddDocumentModal}
        >
          Add Broker Specific Document
        </Button>
      </ButtonBar>
    </>
  );
};
const TobaDetails = ({ show, activeAttributes, onEdit, onEnd, isSubmitting }) => {
  if (!show) {
    return null;
  }
  return (
    <>
      <Row>
        <Col sm={12}>
          <TobaFields activeAttributes={activeAttributes} />
        </Col>
      </Row>
      <ButtonBar>
        <Button
          bsStyle={'primary'}
          handleClick={onEdit}
          className={'pull-right'}
          label={'New Version'}
        />
        <Button
          bsStyle={'primary'}
          handleClick={onEnd}
          className={'pull-right'}
          label={'End Document'}
          isLoading={isSubmitting}
        />
      </ButtonBar>
    </>
  );
};
const BrokerToba = ({
  tobas,
  onEditDocument,
  onEndDocument,
  onCreateNewDocument,
  onRefresh,
  onUpdateDocument,
  wlChannelId,
  productId,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const initialDocument = tobas?.all?.[0];

  const [selectedDocument, setSelectedDocument] = useState(initialDocument);

  const activeDocument = tobas?.active;

  const hasNonActiveTobas = tobas?.all?.length && !activeDocument;

  const isSuperseding = selectedDocument?.is_superseding;
  useEffect(
    () => {
      if (!activeDocument) {
        setSelectedDocument(tobas?.all?.[0]);
      } else {
        const matchedToba = tobas?.all.find((doc) => doc.id === activeDocument?.data?.id);

        if (matchedToba) {
          setSelectedDocument(matchedToba);
        }
      }
    },
    [activeDocument, tobas],
  );

  const {
    documentsGenerated,
    showEditDocumentModal,
    actionType,
    isSubmitting,
  } = useSelector((state) => state.documentManagement);

  const isGenerating = documentsGenerated.includes(activeDocument?.data?.id);
  const showEditModal = () => {
    return onEditDocument('edit-superseding', activeDocument);
  };
  const end = () => {
    return Promise.resolve(onEndDocument(activeDocument?.data?.id)).then(() => {
      return refresh();
    });
  };
  const showAddModal = () => {
    return onEditDocument('add-superseding', activeDocument);
  };

  const refresh = () => {
    setIsRefreshing(true);
    return Promise.resolve(onRefresh()).finally(() => setIsRefreshing(false));
  };

  const saveDocument = (values) => {
    return Promise.resolve(onCreateNewDocument(values)).then(() => {
      return refresh();
    });
  };

  const versionDocument = (values) => {
    const documentId = values.data.id;
    return onUpdateDocument(values, documentId);
  };

  const documentForForm = !isSuperseding
    ? {
        data: {
          id: activeDocument?.data?.id,
          attributes: {
            ...activeDocument?.data?.attributes,
          },
          relationships: {
            channel: {
              data: {
                id: wlChannelId,
                type: 'channels',
              },
            },
            products: {
              data: [
                {
                  id: productId,
                  type: 'products',
                },
              ],
            },
          },
        },
        included: activeDocument?.included,
      }
    : activeDocument;

  const selectDocument = (doc) => {
    setSelectedDocument(doc);
  };

  return (
    <div className={'broker-toba'}>
      <>
        {selectedDocument && isSuperseding && (
          <>
            <DocumentVersionSelector
              documentList={Object.values(tobas?.all).reverse()}
              activeDocument={selectedDocument}
              onSelectDocument={selectDocument}
            />
            <h4>
              <FaFilePdf /> Terms of Business Customer Agreement Document
              <span className={'refresh'} onClick={refresh}>
                <FaRefresh className={isRefreshing ? 'fa-spin' : ''} />
              </span>
            </h4>
            <hr />
            <EndedTobas show={hasNonActiveTobas} document={selectedDocument} />
          </>
        )}
        <TobaDetails
          show={!hasNonActiveTobas && isSuperseding}
          activeAttributes={selectedDocument}
          onEdit={showEditModal}
          onEnd={end}
          isSubmitting={isSubmitting}
        />
      </>

      <DocumentIsGenerating isGenerating={isGenerating} />
      <NoSupersedingToba show={!isSuperseding} onShowAddDocumentModal={showAddModal} />

      {showEditDocumentModal && (
        <EditDocumentModal
          actionType={actionType}
          document={documentForForm}
          show={showEditDocumentModal}
          schemes={[]}
          onEditDocument={versionDocument}
          onAddDocument={saveDocument}
        />
      )}
    </div>
  );
};
export default connect(
  null,
  {
    onEditDocument: documentActions.editDocument,

    //createNewDocument: documentActions.createNewDocument,
    onUpdateDocument: documentActions.updateDocument,
    onCreateNewDocument: documentActions.createNewDocument,
    onEndDocument: documentActions.endDocument,
  },
)(BrokerToba);
